/**
 * fontStack — resolución compartida de tipografías del plan.
 *
 * El editor de estilo (Feature 3) y las plantillas guardan un `id` de fuente
 * corto ("bricolage", "playfair", …). Acá se resuelve a un font-family CSS
 * completo y se garantiza que el <link> de Google Fonts esté cargado.
 *
 * Inter Tight + Bricolage + JetBrains Mono ya vienen en index.html. El resto
 * se inyecta on-demand vía `ensurePlanFontsLoaded()` con un solo <link> de
 * Google Fonts (todas las familias en una request).
 *
 * Expandido en sesión 7 — antes había 6 fuentes; ahora 28 (12 display + 16
 * body), pensado para que el coach tenga variedad real al armar plantillas
 * para distintos públicos (femenino elegante / fitness aggressive / wellness
 * suave / serio profesional / etc.).
 */

export type FontId =
  // --- Sans-serif display (titulares, números grandes, monogramas) ---
  | "bricolage"        // bundled — neutral-friendly
  | "anton"            // very condensed bold, fitness aggressive
  | "bebas"            // condensed uppercase classic
  | "archivo-black"    // heavy modern sans
  | "oswald"           // condensed, deportivo
  | "montserrat"       // geometric, friendly
  | "raleway"          // elegant thin display
  | "poppins"          // geometric round, popular
  | "space-grotesk"    // modern tech display
  | "sora"             // contemporary geometric
  | "jakarta"          // Plus Jakarta Sans — premium body/display
  | "manrope"          // friendly modern sans
  // --- Sans-serif body (cuerpo cómodo de leer) ---
  | "inter"            // bundled — default neutral
  | "dm-sans"          // modern geometric body
  | "ibm-plex"         // technical professional
  | "work-sans"        // versatile body
  | "nunito"           // round friendly body
  | "rubik"            // soft modern body
  // --- Serif (elegancia, magazine, premium femenino) ---
  | "playfair"         // classic editorial high contrast
  | "dm-serif"         // dramatic display serif
  | "cormorant"        // elegant calligraphic
  | "lora"             // warm readable serif
  | "crimson"          // bookish serif
  | "source-serif"     // clean professional serif
  // --- Decorativas (acentos, no body) ---
  | "lobster"          // brush script
  | "pacifico"         // casual handwritten
  // --- Mono ---
  | "jetbrains";       // bundled — code/numbers

export const FONT_OPTIONS: { id: FontId; label: string; category: string }[] = [
  // Sans-serif display
  { id: "bricolage",     label: "Bricolage Grotesque", category: "Display" },
  { id: "anton",         label: "Anton",               category: "Display" },
  { id: "bebas",         label: "Bebas Neue",          category: "Display" },
  { id: "archivo-black", label: "Archivo Black",       category: "Display" },
  { id: "oswald",        label: "Oswald",              category: "Display" },
  { id: "montserrat",    label: "Montserrat",          category: "Display" },
  { id: "raleway",       label: "Raleway",             category: "Display" },
  { id: "poppins",       label: "Poppins",             category: "Display" },
  { id: "space-grotesk", label: "Space Grotesk",       category: "Display" },
  { id: "sora",          label: "Sora",                category: "Display" },
  { id: "jakarta",       label: "Plus Jakarta Sans",   category: "Display" },
  { id: "manrope",       label: "Manrope",             category: "Display" },
  // Sans-serif body
  { id: "inter",         label: "Inter Tight",         category: "Body" },
  { id: "dm-sans",       label: "DM Sans",             category: "Body" },
  { id: "ibm-plex",      label: "IBM Plex Sans",       category: "Body" },
  { id: "work-sans",     label: "Work Sans",           category: "Body" },
  { id: "nunito",        label: "Nunito",              category: "Body" },
  { id: "rubik",         label: "Rubik",               category: "Body" },
  // Serif
  { id: "playfair",      label: "Playfair Display",    category: "Serif" },
  { id: "dm-serif",      label: "DM Serif Display",    category: "Serif" },
  { id: "cormorant",     label: "Cormorant",           category: "Serif" },
  { id: "lora",          label: "Lora",                category: "Serif" },
  { id: "crimson",       label: "Crimson Pro",         category: "Serif" },
  { id: "source-serif",  label: "Source Serif Pro",    category: "Serif" },
  // Decorativas
  { id: "lobster",       label: "Lobster",             category: "Decorativa" },
  { id: "pacifico",      label: "Pacifico",            category: "Decorativa" },
  // Mono
  { id: "jetbrains",     label: "JetBrains Mono",      category: "Mono" },
];

/**
 * Resuelve un id de fuente (o un nombre suelto del label) a un font-family
 * CSS completo. Hace substring match case-insensitive sobre el `label` de
 * cada FONT_OPTIONS, así funciona tanto con id ("bricolage") como con label
 * ("Bricolage Grotesque") — las plantillas históricas guardan el label
 * completo en `brand.displayFont`.
 */
export function fontStack(name: string | undefined | null): string {
  const n = (name || "").toLowerCase();
  // Match por substring contra los labels conocidos (orden importa: chequeo
  // primero las palabras más específicas para no confundir Lora con Lobster).
  if (n.includes("anton"))         return "'Anton', 'Bebas Neue', system-ui, sans-serif";
  if (n.includes("bebas"))         return "'Bebas Neue', 'Anton', system-ui, sans-serif";
  if (n.includes("archivo"))       return "'Archivo Black', system-ui, sans-serif";
  if (n.includes("oswald"))        return "'Oswald', 'Bebas Neue', system-ui, sans-serif";
  if (n.includes("montserrat"))    return "'Montserrat', system-ui, sans-serif";
  if (n.includes("raleway"))       return "'Raleway', system-ui, sans-serif";
  if (n.includes("poppins"))       return "'Poppins', system-ui, sans-serif";
  if (n.includes("space-grotesk") || n.includes("space grotesk")) return "'Space Grotesk', system-ui, sans-serif";
  if (n.includes("jakarta"))       return "'Plus Jakarta Sans', system-ui, sans-serif";
  if (n.includes("manrope"))       return "'Manrope', system-ui, sans-serif";
  if (n.includes("sora"))          return "'Sora', system-ui, sans-serif";
  if (n.includes("dm-sans") || n.includes("dm sans")) return "'DM Sans', system-ui, sans-serif";
  if (n.includes("ibm-plex") || n.includes("ibm plex")) return "'IBM Plex Sans', system-ui, sans-serif";
  if (n.includes("work sans") || n.includes("work-sans")) return "'Work Sans', system-ui, sans-serif";
  if (n.includes("nunito"))        return "'Nunito', system-ui, sans-serif";
  if (n.includes("rubik"))         return "'Rubik', system-ui, sans-serif";
  if (n.includes("bricolage"))     return "'Bricolage Grotesque', system-ui, sans-serif";

  // Serif
  if (n.includes("playfair"))      return "'Playfair Display', Georgia, serif";
  if (n.includes("dm serif") || n.includes("dm-serif")) return "'DM Serif Display', Georgia, serif";
  if (n.includes("cormorant"))     return "'Cormorant', Georgia, serif";
  if (n.includes("lora"))          return "'Lora', Georgia, serif";
  if (n.includes("crimson"))       return "'Crimson Pro', Georgia, serif";
  if (n.includes("source serif") || n.includes("source-serif")) return "'Source Serif Pro', Georgia, serif";

  // Decorativas
  if (n.includes("lobster"))       return "'Lobster', cursive";
  if (n.includes("pacifico"))      return "'Pacifico', cursive";

  // Mono
  if (n.includes("jetbrains"))     return "'JetBrains Mono', ui-monospace, monospace";

  // Default: Inter Tight (body neutral)
  return "'Inter Tight', 'Inter', system-ui, sans-serif";
}

/**
 * Inyecta (una sola vez) el <link> de Google Fonts para TODAS las familias
 * que no están en index.html. UN SOLO request a Google Fonts con todas las
 * familias concatenadas — minimiza round trips. Idempotente: chequea un
 * flag en el <head>. Seguro de llamar en cada render.
 *
 * Bricolage Grotesque + Inter Tight + JetBrains Mono ya vienen pre-cargados
 * en index.html (son el baseline del producto).
 */
export function ensurePlanFontsLoaded(): void {
  if (typeof document === "undefined") return;
  const id = "al-plan-extra-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  // Un mega-URL de Google Fonts con todas las familias extra. Pesos curados
  // para que pese lo menos posible (cubrimos los rangos típicos: 400/600/700/800).
  // 25+ familias → ~150KB total comprimido, cacheado eterno por Google CDN.
  link.href =
    "https://fonts.googleapis.com/css2" +
    "?family=Anton" +
    "&family=Bebas+Neue" +
    "&family=Archivo+Black" +
    "&family=Oswald:wght@400;500;600;700" +
    "&family=Montserrat:wght@400;500;600;700;800" +
    "&family=Raleway:wght@300;400;500;600;700;800" +
    "&family=Poppins:wght@400;500;600;700;800" +
    "&family=Space+Grotesk:wght@400;500;600;700" +
    "&family=Sora:wght@400;500;600;700;800" +
    "&family=Plus+Jakarta+Sans:wght@400;500;600;700;800" +
    "&family=Manrope:wght@400;500;600;700;800" +
    "&family=DM+Sans:wght@400;500;700" +
    "&family=IBM+Plex+Sans:wght@400;500;600;700" +
    "&family=Work+Sans:wght@400;500;600;700" +
    "&family=Nunito:wght@400;600;700;800" +
    "&family=Rubik:wght@400;500;600;700;800" +
    "&family=Playfair+Display:wght@400;600;700;800;900" +
    "&family=DM+Serif+Display" +
    "&family=Cormorant:wght@400;500;600;700" +
    "&family=Lora:wght@400;500;600;700" +
    "&family=Crimson+Pro:wght@400;500;600;700" +
    "&family=Source+Serif+Pro:wght@400;600;700" +
    "&family=Lobster" +
    "&family=Pacifico" +
    "&display=swap";
  document.head.appendChild(link);
}
